export const PHONE_FIELDS = ['phoneNumber', 'homePhone', 'otherPhone'] as const;

export type PhoneField = (typeof PHONE_FIELDS)[number];

const REQUIRED_PHONE = 'At least one phone number is required.';

export const phoneDigits = (value: string) => {
  let digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.slice(0, 10);
};

/** A clean US number, or null when the stored text has an extension or extra digits. */
const nanpDigits = (value: string): string | null => {
  let digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10 || !/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) return null;
  return digits;
};

const formatNanp = (digits: string) =>
  `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;

/** Format a stored number. Leave an extension or a non-US value unchanged. */
export const formatStoredPhone = (value?: string | null): string => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';
  const digits = nanpDigits(trimmed);
  return digits ? formatNanp(digits) : trimmed;
};

export function hasStoredPhone(
  values?: Partial<Record<PhoneField, string | null | undefined>> | null
): boolean {
  return PHONE_FIELDS.some((field) => String(values?.[field] ?? '').trim().length > 0);
}

/**
 * Mobile, then home, then the third number.
 * A valid 10-digit number wins over a partial one. Anything that is not a clean
 * US number is shown as stored, so an extension is not cut off.
 */
export function primaryPhoneDisplay(values?: {
  phoneNumber?: string | null;
  homePhone?: string | null;
  otherPhone?: string | null;
} | null): string {
  const fields = [values?.phoneNumber, values?.homePhone, values?.otherPhone].map((value) =>
    String(value ?? '').trim()
  );
  const valid = fields.find((value) => value && nanpDigits(value));
  if (valid) return formatStoredPhone(valid);
  return fields.find((value) => value) ?? '';
}

export const formatPhoneInput = (value: string) => {
  const digits = phoneDigits(value);
  if (!digits) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const phoneFieldError = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const digits = phoneDigits(trimmed);
  if (digits.length !== 10 || !/^[2-9]\d{2}[2-9]\d{6}$/.test(digits)) {
    return 'Enter a valid 10-digit phone number.';
  }
  return '';
};

/**
 * A blank number is fine. All three blank is blocked only when the patient already
 * had a number, so an imported record with no phone can still save an address.
 */
export function phoneFormErrors(
  values: Partial<Record<PhoneField, string | null | undefined>>,
  options?: { requireOne?: boolean }
): Partial<Record<PhoneField, string>> {
  const errors: Partial<Record<PhoneField, string>> = {};
  for (const field of PHONE_FIELDS) {
    const message = phoneFieldError(String(values[field] ?? ''));
    if (message) errors[field] = message;
  }
  const hasValid = PHONE_FIELDS.some((field) => {
    const value = String(values[field] ?? '').trim();
    return Boolean(value) && !phoneFieldError(value);
  });
  if (options?.requireOne && !hasValid && Object.keys(errors).length === 0) {
    for (const field of PHONE_FIELDS) errors[field] = REQUIRED_PHONE;
  }
  return errors;
}
